import React, { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '@/store/store'
import { logoutUser } from '@/store/slices/authSlice'
import {
    FiUser,
    FiSettings,
    FiLogOut,
    FiX,
    FiGrid,
    FiDollarSign,
    FiStar,
    FiHelpCircle,
    FiShield,
    FiCreditCard
} from 'react-icons/fi'
import Avatar from '@/components/ui/Avatar'
import { SubscriptionBadge } from '@/components/subscription/SubscriptionBadge'

interface ProfileDropdownProps {
    isOpen: boolean
    onClose: () => void
    buttonRef: React.RefObject<HTMLDivElement>
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
    isOpen,
    onClose,
    buttonRef
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const dropdownRef = useRef<HTMLDivElement>(null)
    const { user } = useSelector((state: RootState) => state.auth)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                onClose()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, onClose, buttonRef])

    const handleLogout = async () => {
        await dispatch(logoutUser())
        onClose()
        navigate('/')
    }

    if (!isOpen || !user) return null

    return (
        <div
            ref={dropdownRef}
            className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
        >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                    <Avatar
                        avatarId={user.profile.avatar}
                        alt={`${user.profile.firstName} ${user.profile.lastName}`}
                        size="md"
                        className="bg-primary-100 dark:bg-primary-900"
                        fallbackIcon={<FiUser className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
                    />
                    <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {user.profile.firstName} {user.profile.lastName}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {user.role}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    aria-label="Close profile menu"
                    title="Close"
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                >
                    <FiX className="w-5 h-5" />
                </button>
            </div>

            {/* Subscription Badge */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <SubscriptionBadge user={user} showBg={true} compact={false} />
            </div>

            {/* Profile Stats (if user is a freelancer) */}
            {user.role === 'worker' && (
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">$0</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Earned</p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">0</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Jobs Done</p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-1">
                                <FiStar className="w-3 h-3 text-yellow-500" />
                                0.0
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Rating</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Menu Items */}
            <div className="py-2">
                <Link
                    to="/dashboard"
                    onClick={onClose}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <FiGrid className="mr-3 h-4 w-4" />
                    Dashboard
                </Link>

                <Link
                    to="/dashboard/profile"
                    onClick={onClose}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <FiUser className="mr-3 h-4 w-4" />
                    View Profile
                </Link>

                <Link
                    to="/dashboard/settings"
                    onClick={onClose}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <FiSettings className="mr-3 h-4 w-4" />
                    Account Settings
                </Link>

                <Link
                    to="/subscription"
                    onClick={onClose}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <FiCreditCard className="mr-3 h-4 w-4" />
                    Upgrade Plan
                </Link>

                {user.role === 'worker' && (
                    <>
                        <Link
                            to="/dashboard/earnings"
                            onClick={onClose}
                            className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <FiDollarSign className="mr-3 h-4 w-4" />
                            Earnings
                        </Link>

                        <Link
                            to="/dashboard/reviews"
                            onClick={onClose}
                            className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <FiStar className="mr-3 h-4 w-4" />
                            Reviews
                        </Link>
                    </>
                )}

                <Link
                    to="/dashboard/security"
                    onClick={onClose}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <FiShield className="mr-3 h-4 w-4" />
                    Privacy & Security
                </Link>

                <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

                <Link
                    to="/help"
                    onClick={onClose}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                    <FiHelpCircle className="mr-3 h-4 w-4" />
                    Help & Support
                </Link>

                <button
                    onClick={handleLogout}
                    className="flex items-center w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                    <FiLogOut className="mr-3 h-4 w-4" />
                    Sign Out
                </button>
            </div>
        </div>
    )
}

export default ProfileDropdown